import Teacherheader from "./Teacherheader";
import Teachersidebar from "./Teachersidebar";
import { SuspensionWarningContent } from "./SuspensionWarning";
import { SidebarProvider } from "@/components/ui/sidebar";
import { Outlet } from "react-router-dom";
import { useEffect, useState } from "react";
import { db, auth } from "../firebase/config";
import { collection, onSnapshot, query, where, getDocs } from "firebase/firestore";
import {  onAuthStateChanged, IdTokenResult } from "firebase/auth";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import ContentLoader from "../authentication/ContentLoader";
import EnrollmentRequiredBanner from "../components/EnrollmentRequiredBanner";

interface Affiliation {
  role: string;
  schoolID?: string;
}

interface Dorm {
  id: string;
  name: string;
  location: string;
  capacity: number;
  occupied: number;
  available: number;
  patronTeacherId?: string | null;
  schoolID: string;
  dormUid: string;
  submittedBy: string;
  createdAt: Date;
}

const Teacherdashboard = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [dorms, setDorms] = useState<Dorm[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [schoolID, setSchoolID] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [hasValidClaims, setHasValidClaims] = useState(false);
  const [isSuspended, setIsSuspended] = useState(false);
  const [schoolName, setSchoolName] = useState<string>("");

  // Fetch user and verify claims
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUserId(user.uid);
        try {
          if (process.env.NODE_ENV !== "production") {
            console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Authenticating user with uid:`, user.uid);
          }
          // Refresh token to ensure claims are up-to-date
          await user.getIdToken(true);
          const idTokenResult: IdTokenResult = await user.getIdTokenResult(true);
          if (!idTokenResult) {
            throw new Error("Failed to retrieve user token claims");
          }
          const affiliations = (idTokenResult.claims.affiliations as Affiliation[] | undefined) || [];
          const defaultAffiliation = idTokenResult.claims.defaultAffiliation as Affiliation | undefined;
          const claimSchoolID = idTokenResult.claims.schoolID as string | undefined;

          if (process.env.NODE_ENV !== "production") {
            console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] ID Token Result claims:`, JSON.stringify(idTokenResult.claims, null, 2));
            console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Affiliations:`, affiliations, "Default Affiliation:", defaultAffiliation, "Claim schoolID:", claimSchoolID);
          }

          // Select school_staff affiliation
          let selectedAffiliation: Affiliation | null = null;
          if (defaultAffiliation && defaultAffiliation.role === "school_staff" && defaultAffiliation.schoolID) {
            selectedAffiliation = defaultAffiliation;
          } else if (affiliations.length > 0) {
            selectedAffiliation = affiliations.find(aff => aff.role === "school_staff" && aff.schoolID) || affiliations[0];
          }

          if (selectedAffiliation?.role === "school_staff" && selectedAffiliation.schoolID && claimSchoolID === selectedAffiliation.schoolID) {
            const selectedSchoolID = selectedAffiliation.schoolID;
            if (process.env.NODE_ENV !== "production") {
              console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Using schoolID from selectedAffiliation:`, selectedSchoolID);
            }
            setSchoolID(selectedSchoolID);
            setHasValidClaims(true);
          } else {
            // Retry once after delay to handle claim propagation
            if (process.env.NODE_ENV !== "production") {
              console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Invalid or missing school_staff claims, retrying after 10 seconds...`);
            }
            await new Promise(resolve => setTimeout(resolve, 10000));
            await user.getIdToken(true);
            const retryIdTokenResult = await user.getIdTokenResult(true);
            const retryAffiliations = (retryIdTokenResult.claims.affiliations as Affiliation[] | undefined) || [];
            const retryDefaultAff = retryIdTokenResult.claims.defaultAffiliation as Affiliation | undefined;
            const retrySchoolID = retryIdTokenResult.claims.schoolID as string | undefined;

            if (process.env.NODE_ENV !== "production") {
              console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Retry claims:`, JSON.stringify(retryIdTokenResult.claims, null, 2));
            }

            let retrySelectedAffiliation: Affiliation | null = null;
            if (retryDefaultAff && retryDefaultAff.role === "school_staff" && retryDefaultAff.schoolID) {
              retrySelectedAffiliation = retryDefaultAff;
            } else if (retryAffiliations.length > 0) {
              retrySelectedAffiliation = retryAffiliations.find(aff => aff.role === "school_staff" && aff.schoolID) || retryAffiliations[0];
            }

            if (retrySelectedAffiliation?.role === "school_staff" && retrySelectedAffiliation.schoolID && retrySchoolID === retrySelectedAffiliation.schoolID) {
              setSchoolID(retrySelectedAffiliation.schoolID);
              setHasValidClaims(true);
            } else {
              // Instead of throwing error, set hasValidClaims to false to allow rendering with "No dorms found"
              console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Invalid claims after retry: affiliations=${JSON.stringify(retryAffiliations)}, defaultAffiliation=${JSON.stringify(retryDefaultAff)}, schoolID=${retrySchoolID}`);
              setHasValidClaims(false);
              setSchoolID(null);
            }
          }
        } catch (error: any) {
          console.error(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Error verifying user claims:`, error);
          // Check if it's a network error
          const isNetworkError = error?.code === 'auth/network-request-failed' ||
                                error?.message?.includes('ERR_NAME_NOT_RESOLVED') ||
                                error?.message?.includes('network-request-failed') ||
                                error?.message?.includes('network');
          
          if (isNetworkError) {
            // Don't logout on network errors, just show a warning
            toast({
              title: "Network Error",
              description: "Unable to verify permissions due to network issues. Please check your connection.",
              variant: "destructive",
              duration: 5000,
            });
          } else {
            // Only show permission error for non-network errors
            toast({
              title: "Missing Permissions",
              description: "You lack the necessary permissions to view dormitories. Please contact support.",
              variant: "destructive",
              duration: 5000,
            });
          }
          setHasValidClaims(false);
          setSchoolID(null);
        }
      } else {
        console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] No user logged in - deferring to App.tsx routing`);
        // Don't redirect here - let App.tsx global routing handle it
        // This prevents incorrect redirects during network errors
      }
      setIsLoading(false);
    });

    return () => {
      if (process.env.NODE_ENV !== "production") {
        console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Unsubscribing from onAuthStateChanged`);
      }
      unsubscribe();
    };
  }, [navigate, toast]);

  // Check suspension status when schoolID changes
  useEffect(() => {
    const checkSuspensionStatus = async () => {
      if (!schoolID || !auth.currentUser?.email) {
        setIsSuspended(false);
        return;
      }

      try {
        // Query the teachers collection to find this user by email
        const teachersRef = collection(db, `schools/${schoolID}/teachers`);
        const q = query(teachersRef, where("email", "==", auth.currentUser.email));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const teacherDoc = querySnapshot.docs[0];
          const teacherData = teacherDoc.data();
          setIsSuspended(teacherData.suspended === true);
          console.log("Suspension check:", { email: auth.currentUser.email, suspended: teacherData.suspended });
        } else {
          setIsSuspended(false);
        }

        // Fetch school name
        const schoolDetailsRef = collection(db, `schools/${schoolID}/schoolDetails`);
        const schoolDetailsSnapshot = await getDocs(schoolDetailsRef);
        if (!schoolDetailsSnapshot.empty) {
          const schoolData = schoolDetailsSnapshot.docs[0].data();
          setSchoolName(schoolData.name || "this school");
        }
      } catch (error) {
        console.error("Error checking suspension status:", error);
        setIsSuspended(false);
      }
    };

    checkSuspensionStatus();
  }, [schoolID]);

  // Function to fetch occupancy by counting students with matching dormName
  const fetchOccupancy = async (dormName: string) => {
    if (!schoolID) return 0;
    const studentsRef = collection(db, `schools/${schoolID}/students`);
    const q = query(studentsRef, where("dormitory", "==", dormName));
    const snapshot = await getDocs(q);
    return snapshot.size;
  };

  // Fetch dorms and listen for changes
  useEffect(() => {
    if (!schoolID || isLoading || !hasValidClaims || isSuspended) {
      console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Skipping dorm fetch: schoolID=${schoolID}, isLoading=${isLoading}, hasValidClaims=${hasValidClaims}, isSuspended=${isSuspended}`);
      return;
    }

    const dormsCollectionRef = collection(db, `schools/${schoolID}/dorms`);
    const unsubscribeDorms = onSnapshot(dormsCollectionRef, async (snapshot) => {
      const dormsData: Dorm[] = [];
      for (const doc of snapshot.docs) {
        const data = doc.data() as Dorm;
        const occupied = await fetchOccupancy(data.name);
        dormsData.push({
          id: doc.id,
          name: data.name,
          location: data.location,
          capacity: data.capacity,
          occupied,
          available: data.capacity - occupied,
          patronTeacherId: data.patronTeacherId || null,
          schoolID: data.schoolID,
          dormUid: data.dormUid ?? '',
          submittedBy: data.submittedBy,
          createdAt: data.createdAt,
        });
      }
      console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] School staff access verified - Dorms fetched for user ${userId} (schoolID: ${schoolID}):`, dormsData);
      setDorms(dormsData.sort((a, b) => a.name.localeCompare(b.name)));
      if (dormsData.length === 0) {
        console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] No dorms found for schoolID: ${schoolID} for user ${userId}`);
      }
    }, (error) => {
      console.error(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Error fetching dorms for user ${userId}:`, error);
      console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] School staff access denied or data unavailable for user ${userId}`);
      setDorms([]);
    });

    return () => {
      if (process.env.NODE_ENV !== "production") {
        console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Unsubscribing from dorms onSnapshot`);
      }
      unsubscribeDorms();
    };
  }, [schoolID, isLoading, hasValidClaims, isSuspended, userId, toast]);

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full">
        <div className=" h-full border-r  dark:border-gray-700">
          <Teachersidebar />
        </div>
        <div className="flex flex-col flex-1 h-full">
          <Teacherheader />
          <div className="flex-1 overflow-y-auto p-4 bg-gray-50 dark:bg-gray-950">
            <ContentLoader isLoading={isLoading} message="">
              {isSuspended ? (
                <SuspensionWarningContent schoolName={schoolName || "this school"} />
              ) : !schoolID ? (
                <EnrollmentRequiredBanner role="school_staff" />
              ) : (
                <div>
                  <h2 className="text-xl font-bold mb-4">Dorms</h2>
                  {dorms.length > 0 ? (
                    <ul className="list-disc pl-5">
                      {dorms.map((dorm) => (
                        <li key={dorm.id} className="mb-2">
                          {dorm.name} (Capacity: {dorm.capacity}, Available: {dorm.dormUid || "None"})
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p>No dorms found.</p>
                  )}
                </div>
              )}
            </ContentLoader>
            <Outlet />
          </div>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default Teacherdashboard;